import React from 'react';
import { Box } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import DefaultButton from './DefaultButton';

const PaginationControls = ({
  currentPage,
  setCurrentPage,
  simulations,
  ITEMS_PER_PAGE,
  theme,
}) => {
  if (simulations.length <= ITEMS_PER_PAGE) return null;

  const totalPages = Math.ceil(simulations.length / ITEMS_PER_PAGE);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: `${theme.customSpacing.lg} ${theme.customSpacing['3xl']} ${theme.customSpacing.xl} ${theme.customSpacing['3xl']}`,
      }}
    >
      {/* Previous Button */}
      <Box sx={{ width: 140, display: 'flex', justifyContent: 'flex-start' }}>
        {currentPage !== 0 && (
          <DefaultButton
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
            size="sm"
            backgroundColor="transparent"
            borderColor={theme.palette.border.brand}
            textColor={theme.palette.text.primary}
            startIcon={<ArrowBack sx={{ fontSize: theme.typography.textLG.fontSize }} />}
          >
            Previous
          </DefaultButton>
        )}
      </Box>

      {/* Page Numbers */}
      <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', gap: theme.customSpacing.lg }}>
        {Array.from({ length: totalPages }, (_, i) => {
          const isActive = i === currentPage;
          const isEdge =
            i === 0 || i === totalPages - 1 || Math.abs(i - currentPage) <= 1;

          if (!isEdge && Math.abs(i - currentPage) === 2) {
            return (
              <Box key={`ellipsis-${i}`} sx={{ px: 1, color: theme.palette.text.primary }}>
                ...
              </Box>
            );
          }

          if (!isEdge) return null;

          return (
            <Box
              key={i}
              onClick={() => setCurrentPage(i)}
              sx={{
                px: 3,
                py: 2,
                borderRadius: 1,
                backgroundColor: isActive ? theme.palette.background.brand_alt : 'transparent',
                color: isActive ? theme.palette.text.button_hover : theme.palette.text.tertiary,
                cursor: 'pointer',
                fontWeight: isActive ? 600 : 400,
                fontSize: theme.typography.textXS.fontSize,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: isActive
                    ? theme.palette.background.button_secondary
                    : 'rgba(255, 255, 255, 0.08)',
                },
              }}
            >
              {i + 1}
            </Box>
          );
        })}
      </Box>

      {/* Next Button */}
      <Box sx={{ width: 140, display: 'flex', justifyContent: 'flex-end' }}>
        {(currentPage + 1) * ITEMS_PER_PAGE < simulations.length && (
          <DefaultButton
            onClick={() =>
              setCurrentPage((prev) =>
                prev < totalPages - 1 ? prev + 1 : prev
              )
            }
            size="sm"
            backgroundColor="transparent"
            borderColor={theme.palette.border.brand}
            textColor={theme.palette.text.primary}
            endIcon={<ArrowForward sx={{ fontSize: theme.typography.textLG.fontSize }} />}
          >
            Next
          </DefaultButton>
        )}
      </Box>
    </Box>
  );
};

export default PaginationControls;
